// addCheckConstraint — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'add-check-constraint', check })` op (DSE2 §3.B4).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B4.
//
// Per-engine support (mirrors DSE2's `synthesizeAddCheckConstraint` rules):
//   - sqlite: rejected on ALTER (recreate path opt-in lives in Slice H3).
//   - mssql: optional `mssqlWithNoCheck: boolean` (suppresses legacy data
//     validation on creation).
//   - postgres: optional `pgsqlNoInherit: boolean` (prevents the check from
//     cascading to inheriting tables).
//   - mysql: optional `mysqlNotEnforced: boolean` (mysql 8.0.16+).
//   - oracle: optional `deferrable: 'deferred' | 'immediate'`.

import type {
  CheckConstraintDef,
  DdlEdit,
} from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type AddCheckConstraintArgs = {
  readonly check: CheckConstraintDef;
};

export function buildAddCheckConstraintEdit(args: AddCheckConstraintArgs): DdlEdit {
  return { op: 'add-check-constraint', check: args.check };
}

export async function commitAddCheckConstraint(
  ctx: VqeDdlEditContext,
  args: AddCheckConstraintArgs,
): Promise<void> {
  const edit = buildAddCheckConstraintEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isAddCheckConstraintSupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getAddCheckConstraintUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … ADD CONSTRAINT CHECK. '
      + 'Define the check constraint inline at CREATE TABLE time, or '
      + 'recreate the table with the constraint — use “Edit as SQL” to '
      + 'write the change.'
    );
  }
  return '';
}

export function isMssqlWithNoCheckSupported(engine: SqlEngine): boolean {
  return engine === 'mssql';
}

export function isPgsqlNoInheritSupported(engine: SqlEngine): boolean {
  return engine === 'postgres';
}

export function isMysqlNotEnforcedSupported(engine: SqlEngine): boolean {
  return engine === 'mysql';
}

export function isDeferrableSupported(engine: SqlEngine): boolean {
  return engine === 'oracle';
}

export function getEngineFieldUnsupportedTooltip(
  engine: SqlEngine,
  field: 'mssqlWithNoCheck' | 'pgsqlNoInherit' | 'mysqlNotEnforced' | 'deferrable',
): string {
  switch (field) {
    case 'mssqlWithNoCheck':
      return 'WITH NOCHECK is a SQL Server-only CHECK modifier.';
    case 'pgsqlNoInherit':
      return 'NO INHERIT is a PostgreSQL-only CHECK modifier.';
    case 'mysqlNotEnforced':
      return 'NOT ENFORCED requires MySQL 8.0.16+ and is unsupported on other engines.';
    case 'deferrable':
      return 'DEFERRABLE INITIALLY is supported on Oracle CHECK constraints only.';
  }
  void engine;
  return '';
}

/** Pre-flight validator mirroring DSE2's `validateCheck` rules so the form can
 *  surface engine errors before the synthesizer would throw. Returns null
 *  when the spec is acceptable. */
export function validateCheckConstraintForEngine(
  engine: SqlEngine,
  ck: CheckConstraintDef,
): string | null {
  if (engine === 'sqlite') return getAddCheckConstraintUnsupportedTooltip(engine);
  if (!ck.expression || ck.expression.trim().length === 0) {
    return 'CHECK constraint requires a non-empty expression.';
  }
  if (engine !== 'mssql' && ck.mssqlWithNoCheck !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'mssqlWithNoCheck');
  }
  if (engine !== 'postgres' && ck.pgsqlNoInherit !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'pgsqlNoInherit');
  }
  if (engine !== 'mysql' && ck.mysqlNotEnforced !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'mysqlNotEnforced');
  }
  if (engine !== 'oracle' && ck.deferrable !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'deferrable');
  }
  return null;
}

/** MySQL 8.0.16+ note for the form — surfaces in the NOT ENFORCED section on
 *  mysql so users on older clusters know why the synthesized statement may
 *  silently no-op (pre-8.0.16 mysql parses CHECK but ignores it). A live
 *  engine-version probe isn't plumbed through `engineId` today; the runtime
 *  engine itself accepts the syntax on 8.0.16+ and silently no-ops earlier
 *  versions, so the hint covers the pre-flight UX. */
export function getCheckEngineVersionNote(engine: SqlEngine): string | null {
  if (engine === 'mysql') {
    return 'CHECK constraints are enforced on MySQL 8.0.16+. Earlier versions parse CHECK but silently ignore it.';
  }
  return null;
}
